/**
 * The font vocabulary of the A4 builders (e-mail + document), and the one
 * function that turns a STORED font value into the CSS that is painted.
 *
 * Why this exists. The inspector used to offer 27 "web-safe" family names and
 * persist them verbatim — `roboto`, `lato`, `open sans`, … Sixteen of those are
 * Google Fonts that no desktop OS ships, nothing loaded them into the editor,
 * and nothing embedded them in the mail. A CSS `font-family` that names ONE
 * unavailable family does not inherit the parent's stack: the UA falls back to
 * its default face, which is Times. So "Roboto" painted Times on the canvas, in
 * the dropdown's own preview, and in the recipient's Outlook — measured with
 * Chromium's `CSS.getPlatformFontsForNode` on macOS: 19 of the 27 options (the
 * 16 Google families; the two Japanese entries, localized names no engine
 * resolves; and `lucida sans`, which macOS does not ship).
 *
 * The rules that fall out of that:
 *
 *  1. An option's VALUE is a full stack — real family first, a generic family
 *     last — so the worst case is an honest sans/serif, never Times.
 *  2. A family the inspector offers as a webfont is one we HOST (registered in
 *     `WB_FONT_FILES`), and it offers exactly the weights the hosted file can
 *     draw.
 *  3. Stored values are NOT migrated. `resolveFontStack` maps a legacy bare
 *     token to its stack at every paint/serialize site, so a template nobody
 *     touched is neither rewritten nor flagged dirty by being opened.
 */
import {
  WB_FONT_FILES,
  wbFontFaceCss,
} from "@/components/builders/website/configurator-panel/components/wbFontPairings";
import { EMAIL_FONT_STACKS } from "@/lib/designDirections/directionTheme";
import {
  normalizeFontStack,
  type StyleValue,
} from "@/lib/utils/builder-style-values";

/**
 * `value` is `number | string` for historical reasons: the retired Hiragino
 * entry carried the Japanese weight names "W3"/"W6". Nothing in the catalog
 * does any more, but `fontWeightValue` still guards the shape.
 */
export type FontWeightOption = { value: number | string; label: string };

export type CatalogFont = {
  /** Display name, and — for a hosted face — the `WB_FONT_FILES` key. */
  family: string;
  /** What gets persisted into `fontType` / `globalFont`. */
  stack: string;
  weights: FontWeightOption[];
};

/** First family, unquoted, lowercased. */
export const normalizeFamily = (stack: string): string =>
  stack.split(",")[0].trim().replace(/^['"]+|['"]+$/g, "").toLowerCase();

/** Where a value that fails every gate lands. Available on every platform. */
export const DEFAULT_FONT_STACK = "Arial, Helvetica, sans-serif";

/**
 * The nine CSS weight steps, with the labels `websiteBuilderSettings` carries
 * in every locale. Also what a design-direction stack offers (font-options).
 */
export const ALL_FONT_WEIGHTS: readonly FontWeightOption[] = [
  { value: 100, label: "thin" },
  { value: 200, label: "extra-light" },
  { value: 300, label: "light" },
  { value: 400, label: "regular" },
  { value: 500, label: "medium" },
  { value: 600, label: "semi-bold" },
  { value: 700, label: "bold" },
  { value: 800, label: "extra-bold" },
  { value: 900, label: "black" },
];

const weightsAt = (...values: number[]): FontWeightOption[] =>
  ALL_FONT_WEIGHTS.filter((w) => values.includes(w.value as number));

const REGULAR_BOLD: FontWeightOption[] = weightsAt(400, 700);

/**
 * Every weight step a hosted family's files can draw: each hundred inside a
 * variable face's `wght` range, or the exact weight of a static face.
 *
 * DERIVED from the font table, never typed out. A hand-kept list is how the
 * old dropdown offered Lato Thin against files for 400 and 700 — and a list
 * that is too SHORT is as wrong: a heading stored at 600 that moves to a face
 * able to draw 600 must not be rewritten to 500 by `snapFontWeight` because a
 * list forgot the step.
 */
function hostedWeights(family: string): FontWeightOption[] {
  const faces = WB_FONT_FILES[family];
  if (!faces) throw new Error(`No hosted font files for family: ${family}`);
  return ALL_FONT_WEIGHTS.filter((w) =>
    faces.some((face) => {
      const [min, max] = face.weight.split(" ").map(Number);
      const n = w.value as number;
      return max === undefined ? n === min : n >= min && n <= max;
    }),
  );
}

/** One stack for both Japanese system faces: Hiragino on Apple, Meiryo / Yu
 *  Gothic on Windows. Replaces two localized family names (`ヒラギノ角ゴ pro w3`
 *  — where "W3" is a weight, not part of the family — and `メイリオ`) that
 *  resolved in no current engine. */
const JAPANESE_STACK =
  "'Hiragino Kaku Gothic ProN', 'Hiragino Sans', Meiryo, 'Yu Gothic', sans-serif";

/** Families every desktop platform ships, or whose stack names the platform
 *  twin right behind it (`'Lucida Sans', 'Lucida Grande', …`). */
export const WEB_SAFE_CATALOG: readonly CatalogFont[] = [
  { family: "Arial", stack: "Arial, Helvetica, sans-serif", weights: REGULAR_BOLD },
  { family: "Courier", stack: "Courier, 'Courier New', monospace", weights: REGULAR_BOLD },
  { family: "Helvetica", stack: "Helvetica, Arial, sans-serif", weights: REGULAR_BOLD },
  {
    family: "Hiragino Kaku Gothic ProN / Meiryo",
    stack: JAPANESE_STACK,
    weights: REGULAR_BOLD,
  },
  {
    family: "Lucida Sans",
    stack: "'Lucida Sans', 'Lucida Grande', 'Lucida Sans Unicode', sans-serif",
    weights: REGULAR_BOLD,
  },
  { family: "Tahoma", stack: "Tahoma, Geneva, Verdana, sans-serif", weights: REGULAR_BOLD },
  { family: "Times New Roman", stack: "'Times New Roman', Times, serif", weights: REGULAR_BOLD },
  { family: "Trebuchet MS", stack: "'Trebuchet MS', Helvetica, sans-serif", weights: REGULAR_BOLD },
  { family: "Verdana", stack: "Verdana, Geneva, sans-serif", weights: REGULAR_BOLD },
];

/**
 * Self-hosted webfonts. `family` is the `WB_FONT_FILES` key, and `weights` is
 * exactly what that family's files can draw (see `hostedWeights`).
 *
 * Two successors stand in for names that no longer exist upstream: Noto Serif
 * for Droid Serif, Source Sans 3 for Source Sans Pro. Ubuntu is absent on
 * purpose — see NOTICE-builder-fonts.txt (Ubuntu Font Licence 1.0, clause 2.3).
 */
export const HOSTED_CATALOG: readonly CatalogFont[] = (
  [
    ["Abril Fatface", "'Abril Fatface', Didot, Georgia, serif"],
    ["Bitter", "Bitter, Georgia, 'Times New Roman', serif"],
    ["Cabin", "Cabin, 'Trebuchet MS', Helvetica, Arial, sans-serif"],
    // Static 400 / 700 only.
    ["Lato", "Lato, 'Helvetica Neue', Helvetica, Arial, sans-serif"],
    ["Merriweather", "Merriweather, Georgia, 'Times New Roman', serif"],
    ["Noto Serif", "'Noto Serif', Georgia, 'Times New Roman', serif"],
    ["Nunito", "Nunito, 'Trebuchet MS', Helvetica, Arial, sans-serif"],
    ["Open Sans", "'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif"],
    ["Oswald", "Oswald, 'Arial Narrow', Impact, Arial, sans-serif"],
    // Static 400 / 700 only: the Light file's latin subset lacks € and –.
    ["Oxygen", "Oxygen, 'Helvetica Neue', Helvetica, Arial, sans-serif"],
    ["Permanent Marker", "'Permanent Marker', 'Marker Felt', 'Comic Sans MS', cursive"],
    ["Roboto", "Roboto, 'Helvetica Neue', Helvetica, Arial, sans-serif"],
    ["Roboto Slab", "'Roboto Slab', Rockwell, Georgia, serif"],
    ["Source Sans 3", "'Source Sans 3', 'Helvetica Neue', Helvetica, Arial, sans-serif"],
  ] as const
).map(([family, stack]) => ({ family, stack, weights: hostedWeights(family) }));

const stackOf = (family: string): string => {
  const hit =
    WEB_SAFE_CATALOG.find((f) => f.family === family) ??
    HOSTED_CATALOG.find((f) => f.family === family);
  if (!hit) throw new Error(`No catalog stack for family: ${family}`);
  return hit.stack;
};

/**
 * Every bare token the old dropdown could persist → the stack it means now.
 *
 * This is the whole migration surface, and it is CLOSED: the key set is pinned
 * against the retired list in the test. A new font never gets a key here — it
 * is offered as a stack from day one.
 *
 * `ubuntu` keeps an honest stack (Ubuntu where the OS has it, a sans otherwise)
 * but no dropdown option, so a template that stored it stays readable without
 * the inspector ever offering a face we may not serve.
 */
export const LEGACY_FONT_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  "abril fatface": stackOf("Abril Fatface"),
  arial: stackOf("Arial"),
  bitter: stackOf("Bitter"),
  cabin: stackOf("Cabin"),
  courier: stackOf("Courier"),
  "droid serif": stackOf("Noto Serif"),
  georgia: EMAIL_FONT_STACKS.Georgia.stack,
  helvetica: stackOf("Helvetica"),
  lato: stackOf("Lato"),
  "lucida sans": stackOf("Lucida Sans"),
  merriweather: stackOf("Merriweather"),
  montserrat: EMAIL_FONT_STACKS.Montserrat.stack,
  nunito: stackOf("Nunito"),
  "open sans": stackOf("Open Sans"),
  oswald: stackOf("Oswald"),
  oxygen: stackOf("Oxygen"),
  "permanent marker": stackOf("Permanent Marker"),
  roboto: stackOf("Roboto"),
  "roboto slab": stackOf("Roboto Slab"),
  "source sans pro": stackOf("Source Sans 3"),
  tahoma: stackOf("Tahoma"),
  "times new roman": stackOf("Times New Roman"),
  "trebuchet ms": stackOf("Trebuchet MS"),
  ubuntu: "Ubuntu, 'Segoe UI', Helvetica, Arial, sans-serif",
  verdana: stackOf("Verdana"),
  "ヒラギノ角ゴ pro w3": JAPANESE_STACK,
  "メイリオ": JAPANESE_STACK,
});

// A Map, not the record: `LEGACY_FONT_ALIASES["constructor"]` is a function.
const ALIAS = new Map(Object.entries(LEGACY_FONT_ALIASES));

/**
 * lowercased family → its stack, for every family the builders know: the
 * design-direction registry and both catalogs. The AI lanes accept any safe
 * string, so a model that writes `Inter` or `Helvetica Neue` stores a bare
 * family that was never one of the 27 dropdown tokens — and paints Times for a
 * reader without it just the same.
 */
const KNOWN_FAMILY_STACK = new Map<string, string>([
  ...Object.values(EMAIL_FONT_STACKS).map((m) => [normalizeFamily(m.stack), m.stack] as const),
  ...[...WEB_SAFE_CATALOG, ...HOSTED_CATALOG].map(
    (f) => [normalizeFamily(f.stack), f.stack] as const,
  ),
]);

/** The tokens that rendered correctly even bare: macOS, Windows AND iOS ship
 *  these, so a stored `font-family: arial;` is not worth rewriting a template
 *  for. */
const BARE_BUT_FINE = new Set([
  "arial",
  "courier",
  "georgia",
  "helvetica",
  "times new roman",
  "trebuchet ms",
  "verdana",
]);

/**
 * The legacy tokens that actually painted Times for someone — the 16 Google
 * families, the two Japanese names, `lucida sans` (absent on macOS) and
 * `tahoma` (absent on iOS). Only these make stored HTML worth regenerating;
 * see `fontHtmlStale.ts`.
 */
export const STALE_FONT_TOKENS: ReadonlySet<string> = new Set(
  Object.keys(LEGACY_FONT_ALIASES).filter((k) => !BARE_BUT_FINE.has(k)),
);

/**
 * A bare family we know → its stack; anything else unchanged.
 *
 * "Bare" means no comma: a value that already is a stack is its author's
 * choice, even when it starts with a known family, and is never rewritten.
 * The legacy alias wins over the family table — it is what renames `droid
 * serif` and `source sans pro`. A bare family nobody knows is left alone:
 * there is no stack to give it.
 */
export function canonicalFontStack(value: string): string {
  if (value.includes(",")) return value;
  const family = normalizeFamily(value);
  return ALIAS.get(family) ?? KNOWN_FAMILY_STACK.get(family) ?? value;
}

/** True when `value` is one of the legacy tokens that painted Times. */
export function isStaleFontToken(value: unknown): boolean {
  return (
    typeof value === "string" &&
    !value.includes(",") &&
    STALE_FONT_TOKENS.has(normalizeFamily(value))
  );
}

/**
 * The CSS `font-family` to paint for a stored value.
 *
 * `stored` is a node's own `fontType`; `fallback` is what it inherits when
 * unset (the template's `globalFont`). Both go through the same gate the write
 * lanes use (`normalizeFontStack`: character allow-list, length cap, `"` → `'`)
 * and then the alias step, so the result always satisfies `isSafeFontStack` —
 * it is interpolated verbatim into `style="font-family: …"`.
 */
export function resolveFontStack(stored: unknown, fallback: unknown): string {
  return gated(stored) ?? gated(fallback) ?? DEFAULT_FONT_STACK;
}

/**
 * A value through the gate and the alias step, or undefined. A stray comma at
 * either end is dropped first: `Roboto,` passes the character allow-list and
 * would emit `font-family: Roboto,;` — a declaration every client discards.
 * Only the ENDS are touched; a stack's own spacing is left byte-for-byte.
 */
function gated(value: unknown): string | undefined {
  const v = normalizeFontStack(value);
  if (v.kind !== "ok") return undefined;
  const trimmed = v.value.replace(/^[\s,]+|[\s,]+$/g, "");
  return trimmed === "" ? undefined : canonicalFontStack(trimmed);
}

/** The alias step for a write lane's already-gated value. */
export function canonicalizeFontValue(v: StyleValue<string>): StyleValue<string> {
  return v.kind === "ok" ? { kind: "ok", value: canonicalFontStack(v.value) } : v;
}

/**
 * `WB_FONT_FILES` keys whose file is a LOOKALIKE served under a licensed or
 * system family's name (Gelasio as Georgia, Jost as Futura, Bodoni Moda as
 * Didot, Archivo as Neue Haas Grotesk). They exist for PDF / website parity and
 * must never reach a recipient's mail client or be layered onto the editor on
 * top of the parity faces: the reader has the real Georgia.
 */
export const WEBFONT_EXCLUDED_FAMILIES: ReadonlySet<string> = new Set([
  "Georgia",
  "Futura",
  "Didot",
  "Neue Haas Grotesk",
]);

// ── Webfonts in the SENT mail ──────────────────────────────────────────────
//
// The canvas loads every hosted face, so the author sees Roboto. The reader
// only does if the mail itself declares it: `@font-face` in the head, pointing
// at our own asset host. Apple Mail, iOS Mail, Thunderbird and Outlook for Mac
// load it (about half of all opens); Gmail, Yahoo and Outlook.com ignore the
// rule and walk the stack — which is why the stack is what it is.

/** lowercased family → its `WB_FONT_FILES` key, lookalikes excluded. */
const EMBEDDABLE = new Map(
  Object.keys(WB_FONT_FILES)
    .filter((family) => !WEBFONT_EXCLUDED_FAMILIES.has(family))
    .map((family) => [family.toLowerCase(), family] as const),
);

/**
 * The hosted family a stack LEADS with, if any.
 *
 * Only the first family counts. `Didot, 'Playfair Display', Georgia, serif`
 * asks for Didot; embedding Playfair because it happens to be second would
 * make a Mac — which has Didot — download a face it will not use, and a
 * Windows reader see a font the author did not pick as primary.
 */
export function hostedFamilyOf(stack: unknown): string | undefined {
  if (typeof stack !== "string" || stack === "") return undefined;
  return EMBEDDABLE.get(normalizeFamily(stack));
}

/**
 * `@font-face` rules for the given hosted families — sorted and de-duplicated,
 * so the same template always renders the same bytes. `null` when there is
 * nothing to declare or no asset host to point at.
 */
export function emailFontFaceCss(
  families: Iterable<string>,
  assetBase: string | undefined,
): string | null {
  const hosted = [...new Set(families)]
    .filter((family) => EMBEDDABLE.get(family.toLowerCase()) === family)
    .sort((a, b) => a.localeCompare(b, "en"));
  if (hosted.length === 0) return null;
  return wbFontFaceCss(hosted, assetBase);
}

/**
 * Whether a template's mail may reference a remote font at all.
 *
 * `inlineImagesEnabled` is the author saying "no remote loads": images travel
 * inside the message so no reader sees a download prompt and nothing is
 * fetched on open. A font URL would be the one remote request left — so such
 * a template gets its stacks and no `@font-face`.
 */
export function emailWebfontsAllowed(
  template: { inlineImagesEnabled?: boolean | null } | null | undefined,
): boolean {
  return template?.inlineImagesEnabled !== true;
}

/**
 * The empty downlevel-revealed pair `parse-email`'s head ships, verbatim. Word
 * (Outlook for Windows) treats it as a false conditional and skips to the
 * `<![endif]`; every other client reads straight through it. That is exactly
 * where a `@font-face` has to live: a Word engine that SEES the rule falls
 * back to Times New Roman for the whole stack.
 */
export const WEBFONT_SLOT_EMPTY = "    <!--[if !mso]><!-->\n    <!--<![endif]-->";

/** The same pair carrying a webfont block. */
export const webfontSlot = (css: string): string =>
  `    <!--[if !mso]><!-->\n    <style>\n${css}\n    </style>\n    <!--<![endif]-->`;

/**
 * Matches a rendered webfont block in stored html. Whitespace-tolerant: the
 * backend re-serializes the head, so the exact indentation is not guaranteed.
 * Anchored on the conditional so an author's own `@font-face` in a raw HTML
 * block is never mistaken for ours.
 */
export const WEBFONT_BLOCK_RE = /<!--\[if !mso\]><!-->\s*<style>\s*@font-face/;

/**
 * The weight to keep when a node's family changes.
 *
 * A weight the new family does not offer leaves the weight dropdown showing
 * "regular" over a stored 300 — or, for a one-weight display face, hides the
 * control entirely while a stored 700 renders as synthesized bold. So the
 * stored weight follows the family: unchanged when offered, otherwise the
 * nearest offered weight (the lighter one on a tie). An unset weight stays
 * unset — it means "inherit", which every family can do.
 */
export function snapFontWeight(
  current: unknown,
  weights: readonly FontWeightOption[],
): number | undefined {
  if (current === undefined || current === null || current === "") return undefined;
  // The two CSS keywords a hand-edited or imported tree can carry.
  const keyword = current === "bold" ? 700 : current === "normal" ? 400 : current;
  const n = Number(keyword);
  // Not a weight at all (`"W3"`, `0`, `false`) — nothing to snap; unset means
  // inherit, which every family can do.
  if (!Number.isFinite(n) || n <= 0) return undefined;
  const offered = weights.map((w) => Number(w.value)).filter(Number.isFinite);
  if (offered.length === 0 || offered.includes(n)) return n;
  return offered.reduce((best, w) => {
    const d = Math.abs(w - n);
    const bd = Math.abs(best - n);
    return d < bd || (d === bd && w < best) ? w : best;
  });
}
